import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { 
  Shield, 
  Lock, 
  Eye, 
  EyeOff,
  Database,
  Server,
  Cloud,
  Trash2,
  Download,
  AlertTriangle,
  CheckCircle,
  Info,
  Bot,
  FileText,
  Globe,
  Key
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivacySettings = () => {
  const { token, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    aiEnabled: true,
    aiPrivacyLevel: 'limited', // 'full', 'limited', 'off'
    dataRetentionDays: 90,
    shareAnonymizedData: false,
    allowThirdPartyAI: true,
    showAIDataPreview: true,
    autoDeleteOldData: false,
    exportDataOnDelete: true
  });

  const [dataStats, setDataStats] = useState({
    totalRecords: 0,
    aiQueriesThisMonth: 0,
    dataStoredMB: 0,
    lastAIQuery: null
  });

  useEffect(() => {
    // Load saved settings from localStorage or API
    const saved = localStorage.getItem('cartie_privacy_settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
    
    // Mock data stats
    setDataStats({
      totalRecords: 1247,
      aiQueriesThisMonth: 23,
      dataStoredMB: 12.4,
      lastAIQuery: new Date().toISOString()
    });
  }, []);

  const saveSettings = () => {
    setSaving(true);
    setTimeout(() => {
      localStorage.setItem('cartie_privacy_settings', JSON.stringify(settings));
      toast.success('Privacy settings saved successfully');
      setSaving(false);
    }, 500);
  };

  const handleDeleteAllData = () => {
    if (window.confirm('Are you sure you want to delete ALL your data? This cannot be undone.')) {
      toast.success('Data deletion request submitted. You will receive an email confirmation.');
    }
  };

  const handleExportData = () => {
    toast.success('Data export started. You will receive a download link via email.');
  };

  return (
    <div className="space-y-6" data-testid="privacy-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
          <Shield className="w-8 h-8 text-emerald-500" />
          Privacy & Security
        </h1>
        <p className="text-slate-400 dark:text-slate-400 mt-1">
          Control how your data is used and protected
        </p>
      </div>

      {/* Trust Badges */}
      <div className="flex flex-wrap gap-3">
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-3 py-1">
          <CheckCircle className="w-3 h-3 mr-1" /> SOC 2-aligned controls
        </Badge>
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1">
          <Lock className="w-3 h-3 mr-1" /> AES-256 Encryption
        </Badge>
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-cyan-900/30 dark:text-cyan-400 px-3 py-1">
          <Globe className="w-3 h-3 mr-1" /> GDPR &amp; CCPA rights honoured
        </Badge>
        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-3 py-1">
          <Shield className="w-3 h-3 mr-1" /> TLS 1.3
        </Badge>
      </div>

      {/* AI Privacy Controls */}
      <Card className="border-emerald-200 dark:border-emerald-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-emerald-500" />
            AI Privacy Controls
          </CardTitle>
          <CardDescription>
            Control what data our AI can access and how it's used
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* AI Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${settings.aiEnabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                <Bot className={`w-5 h-5 ${settings.aiEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">AI Features</p>
                <p className="text-sm text-slate-400">Enable AI-powered insights and chat</p>
              </div>
            </div>
            <Switch
              checked={settings.aiEnabled}
              onCheckedChange={(checked) => setSettings({...settings, aiEnabled: checked})}
            />
          </div>

          {settings.aiEnabled && (
            <>
              {/* AI Privacy Level */}
              <div className="space-y-3">
                <Label className="text-base font-medium">AI Data Access Level</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { 
                      value: 'full', 
                      label: 'Full Access', 
                      desc: 'AI sees detailed cost data',
                      icon: Eye,
                      color: 'amber'
                    },
                    { 
                      value: 'limited', 
                      label: 'Limited (Recommended)', 
                      desc: 'AI sees only summaries & trends',
                      icon: EyeOff,
                      color: 'emerald'
                    },
                    { 
                      value: 'off', 
                      label: 'Anonymized Only', 
                      desc: 'AI sees no identifiable data',
                      icon: Lock,
                      color: 'blue'
                    }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSettings({...settings, aiPrivacyLevel: option.value})}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        settings.aiPrivacyLevel === option.value
                          ? `border-${option.color}-500 bg-${option.color}-50 dark:bg-${option.color}-900/20`
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <option.icon className={`w-5 h-5 mb-2 ${
                        settings.aiPrivacyLevel === option.value ? `text-${option.color}-600` : 'text-slate-400'
                      }`} />
                      <p className="font-medium text-slate-900 dark:text-white">{option.label}</p>
                      <p className="text-xs text-slate-400 mt-1">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* What AI Can See */}
              <div className="p-4 bg-emerald-50 dark:bg-blue-900/20 rounded-lg border border-emerald-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800 dark:text-blue-300 mb-2">
                      What AI {settings.aiPrivacyLevel === 'off' ? 'Cannot' : 'Can'} See ({settings.aiPrivacyLevel} mode)
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="font-medium text-emerald-700 dark:text-blue-400 mb-1">✓ AI Can Access:</p>
                        <ul className="text-emerald-600 dark:text-blue-300 space-y-1">
                          {settings.aiPrivacyLevel === 'full' ? (
                            <>
                              <li>• Detailed cost breakdowns</li>
                              <li>• Resource-level data</li>
                              <li>• Team allocations</li>
                              <li>• Historical trends</li>
                            </>
                          ) : settings.aiPrivacyLevel === 'limited' ? (
                            <>
                              <li>• Aggregated summaries</li>
                              <li>• Cost trends & patterns</li>
                              <li>• Anomaly alerts</li>
                              <li>• General recommendations</li>
                            </>
                          ) : (
                            <>
                              <li>• Industry benchmarks</li>
                              <li>• General best practices</li>
                              <li>• Public cloud pricing</li>
                            </>
                          )}
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-emerald-700 dark:text-blue-400 mb-1">✗ AI Cannot Access:</p>
                        <ul className="text-emerald-600 dark:text-blue-300 space-y-1">
                          <li>• AWS / Azure / GCP / Snowflake / Databricks / DigitalOcean credentials</li>
                          <li>• Billing account numbers</li>
                          <li>• Employee personal info</li>
                          {settings.aiPrivacyLevel !== 'full' && (
                            <>
                              <li>• Individual resource IDs</li>
                              <li>• Specific instance names</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Party AI Toggle */}
              <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">Allow Third-Party AI Processing</p>
                  <p className="text-sm text-slate-400">Send data to Claude AI for analysis (encrypted)</p>
                </div>
                <Switch
                  checked={settings.allowThirdPartyAI}
                  onCheckedChange={(checked) => setSettings({...settings, allowThirdPartyAI: checked})}
                />
              </div>

              {/* Show AI Data Preview */}
              <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">Preview Data Before AI Query</p>
                  <p className="text-sm text-slate-400">See exactly what data is sent to AI</p>
                </div>
                <Switch
                  checked={settings.showAIDataPreview}
                  onCheckedChange={(checked) => setSettings({...settings, showAIDataPreview: checked})}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" />
            Data Retention
          </CardTitle>
          <CardDescription>
            Control how long we store your data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base">Keep data for</Label>
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                {settings.dataRetentionDays} days
              </span>
            </div>
            <Slider
              value={[settings.dataRetentionDays]}
              onValueChange={(value) => setSettings({...settings, dataRetentionDays: value[0]})}
              min={30}
              max={365}
              step={30}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-slate-400">
              <span>30 days</span>
              <span>1 year</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 dark:text-white">Auto-Delete Old Data</p>
              <p className="text-sm text-slate-400">Automatically remove data older than retention period</p>
            </div>
            <Switch
              checked={settings.autoDeleteOldData}
              onCheckedChange={(checked) => setSettings({...settings, autoDeleteOldData: checked})}
            />
          </div>
        </CardContent>
      </Card>

      {/* Data Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            Your Data
          </CardTitle>
          <CardDescription>
            Overview of data we store for your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{dataStats.totalRecords.toLocaleString()}</p>
              <p className="text-sm text-slate-400">Total Records</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{dataStats.aiQueriesThisMonth}</p>
              <p className="text-sm text-slate-400">AI Queries (Month)</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{dataStats.dataStoredMB} MB</p>
              <p className="text-sm text-slate-400">Data Stored</p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
              <p className="text-2xl font-bold text-emerald-600">{settings.dataRetentionDays}d</p>
              <p className="text-sm text-slate-400">Retention Period</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Actions */}
      <Card className="border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Data Management
          </CardTitle>
          <CardDescription>
            Export or delete your data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" className="flex-1" onClick={handleExportData}>
              <Download className="w-4 h-4 mr-2" />
              Export All My Data
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
              onClick={handleDeleteAllData}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All My Data
            </Button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Data export includes all cost data, settings, and AI chat history. Deletion is permanent and cannot be undone.
          </p>
        </CardContent>
      </Card>

      {/* Security Practices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-500" />
            Our Security Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: Lock, title: 'Encryption at Rest', desc: 'All data encrypted with AES-256' },
              { icon: Shield, title: 'Encryption in Transit', desc: 'TLS 1.3 for all connections' },
              { icon: Key, title: 'No Credential Storage', desc: 'We never store your cloud credentials' },
              { icon: Server, title: 'SOC 2 Compliant', desc: 'Annual third-party audits' },
              { icon: Eye, title: 'Access Logging', desc: 'All access is logged and auditable' },
              { icon: Trash2, title: 'Right to Delete', desc: 'Delete your data anytime' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                <item.icon className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{item.title}</p>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          className="bg-emerald-600 hover:bg-emerald-700 px-8"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Privacy Settings'}
        </Button>
      </div>
    </div>
  );
};

export default PrivacySettings;
